import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Save,
  X,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Briefcase,
  GraduationCap,
  Heart,
  Info,
  ChevronUp,
  ChevronRight,
} from "lucide-react";
import toast from "react-hot-toast";
import LoaderService from "../services/LoaderService";
import { GetProfileDetails, GetDropdownData } from "./apis/get";
import { PostUpdateProfileDetails } from "./apis/post";

const EditProfileScreen: React.FC = () => {
  const { userIdParams } = useParams<{ userIdParams: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [profileData, setProfileData] = useState<any>(null);
  const [dropdownData, setDropdownData] = useState<any>(null);
  const [initialFormData, setInitialFormData] = useState<any>(null);

  // Form states
  const [formData, setFormData] = useState({
    info: {
      name: "",
      email: "",
      mobile: "",
      gender: "",
      dob: "",
      birth_time: "",
      birth_place: "",
    },
    profile: {
      height_cm: "",
      weight_kg: "",
      complexion: "",
      marital_status: "",
      blood_group: "",
      disability_status: "",
      managed_by: "",
      severity: "",
      is_astrology_imp: false,
      has_child: false,
      intercaste: false,
      maternal_gotra: "",
      occupation: "",
      qualification: "",
      working_with: "",
      annual_income: "",
      annual_income_min: "",
      annual_income_max: "",
      family_type: "",
      family_background: "",
      father_name: "",
      mother_name: "",
      father_occupation: "",
      mother_occupation: "",
      family_income_min: "",
      family_income_max: "",
      sibling_count: "",
      sub_caste: "",
      gotra: "",
      about_me: "",
      about_family: "",
      community: "",
      additional_qualification: "",
      college_name: "",
      field_of_study: "",
      occupation_company: "",
      occupation_location: "",
      address: "",
      city: "",
      state: "",
      country: "",
      home_town: "",
      native_place: "",
      dietary: "",
      drinking: "",
      smoking: "",
      hobbies: "",
      languages_known: "",
      manglik: "",
      alternate_contact: "",
      country_code: "",
      location_type: "",
      latitude: "",
      longitude: "",
      birth_latitude: "",
      birth_longitude: "",
      is_gunn_matching_important: false,
      is_questionnaire_complete: false,
      tags_allocated: "",
      children: [] as any[],
    },
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const [expandedSections, setExpandedSections] = useState({
    personal: true,
    about: true,
    professional: false,
    family: false,
    contact: false,
    lifestyle: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // Helper to render dropdown options robustly
  const renderOptions = (options: any[]) => {
    if (!options || !Array.isArray(options)) return null;
    return options.map((opt, idx) => {
      const value = typeof opt === "object" ? opt.value : opt;
      const label = typeof opt === "object" ? opt.label : opt;
      return (
        <option key={idx} value={value}>
          {label}
        </option>
      );
    });
  };

  // Helper to get normalized value from data object supporting different casings (mirroring UserDetails logic)
  const getApiValue = (data: any, key: string) => {
    if (!data) return undefined;

    // 1. Try exact match
    if (data[key] !== undefined) return data[key];

    // 2. Try snake_case (assuming key is PascalCase or camelCase)
    const snakeKey = key
      .replace(/([A-Z])/g, "_$1")
      .toLowerCase()
      .replace(/^_/, "");
    if (data[snakeKey] !== undefined) return data[snakeKey];

    // 3. Try lowercase
    const lowerKey = key.toLowerCase();
    if (data[lowerKey] !== undefined) return data[lowerKey];

    // 4. Broad case-insensitive search
    const insensitiveKey = Object.keys(data).find(
      (k) => k.toLowerCase() === key.toLowerCase(),
    );
    if (insensitiveKey) return data[insensitiveKey];

    return undefined;
  };

  useEffect(() => {
    if (userIdParams) {
      const initData = async () => {
        setLoading(true);
        LoaderService.show();
        try {
          let currentDropdowns = {};
          const dropdownRes = await GetDropdownData();
          if (dropdownRes.status === true || dropdownRes.status === "success") {
            currentDropdowns = dropdownRes.data || {};
            setDropdownData(currentDropdowns);
          }
          await fetchProfileDetails(currentDropdowns);
        } catch (error) {
          console.error("Initialization error:", error);
        } finally {
          setLoading(false);
          LoaderService.hide();
        }
      };
      initData();
    }
  }, [userIdParams]);

  const fetchProfileDetails = async (dropdowns: any = {}) => {
    try {
      const response = await GetProfileDetails(userIdParams);
      if (response.status === "success" || response.status === true) {
        const data = response.data || {};
        setProfileData(data);

        // Map data to form - mirroring UserDetails.tsx logic
        const matrimonial = data.matrimonial || {};
        const apiProfile = data.profile || {};
        const info = data.info || {};
        // Merge data, profile and matrimonial, with profile taking priority
        const p = { ...data, ...matrimonial, ...apiProfile };

        // Helper function for deep value extraction
        const getValue = (key: string) => {
          // Priority: 1. p[key], 2. info[key], 3. p[snake_case], 4. case-insensitive
          const exact = p[key] ?? info[key];
          if (exact !== undefined) return exact;

          const snakeKey = key
            .replace(/([A-Z])/g, "_$1")
            .toLowerCase()
            .replace(/^_/, "");
          const snake = p[snakeKey] ?? info[snakeKey];
          if (snake !== undefined) return snake;

          const insensitiveKey =
            Object.keys(p).find((k) => k.toLowerCase() === key.toLowerCase()) ||
            Object.keys(info).find(
              (k) => k.toLowerCase() === key.toLowerCase(),
            );
          if (insensitiveKey) return p[insensitiveKey] ?? info[insensitiveKey];

          return undefined;
        };

        // Normalization helper for dropdowns to match options
        const normalizeForDropdown = (
          field: string,
          val: any,
          options: any[] = [],
        ) => {
          if (val === undefined || val === null || val === "") return "";
          let strVal = String(val).trim();
          const upperVal = strVal.toUpperCase().replace(/\s+/g, "_");

          // 1. Handle Income Mapping (Numeric/Formatted to Range)
          if (field === "income") {
            const num = Number(strVal.replace(/[^0-9]/g, ""));
            if (!isNaN(num) && num > 0) {
              const lakh = num / 100000;
              let rangeStr = "";
              if (lakh >= 1 && lakh < 5) rangeStr = "1-5 Lac";
              else if (lakh >= 5 && lakh < 10) rangeStr = "5-10 Lac";
              else if (lakh >= 10 && lakh < 15) rangeStr = "10-15 Lac";
              else if (lakh >= 15 && lakh < 20) rangeStr = "15-20 Lac";
              else if (lakh >= 20 && lakh < 25) rangeStr = "20-25 Lac";
              else if (lakh >= 25 && lakh < 30) rangeStr = "25-30 Lac";
              else if (lakh >= 30 && lakh < 35) rangeStr = "30-35 Lac";
              else if (lakh >= 35 && lakh < 40) rangeStr = "35-40 Lac";
              else if (lakh >= 40 && lakh < 45) rangeStr = "40-45 Lac";
              else if (lakh >= 45 && lakh < 50) rangeStr = "45-50 Lac";
              else if (lakh >= 50) rangeStr = "50 Lac or above";
              
              // Match against dropdown options
              if (rangeStr && options && Array.isArray(options) && options.length > 0) {
                const found = options.find((opt) => {
                  const optVal = typeof opt === "object" ? opt.value : opt;
                  return String(optVal).toLowerCase() === rangeStr.toLowerCase();
                });
                if (found) return typeof found === "object" ? found.value : found;
              }
              return rangeStr;
            }
          }

          // 2. Handle Specific Field Enums (Mapping API values to Dropdown values)
          if (field === "marital_status") {
            if (options && Array.isArray(options) && options.length > 0) {
              const normalize = (s: string) => s.toLowerCase().replace(/[_\s]+/g, "");
              // Match against both value and label
              const found = options.find((opt) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return normalize(String(optVal)) === normalize(strVal) ||
                       normalize(String(optLabel)) === normalize(strVal);
              });
              if (found) return typeof found === "object" ? found.value : found;
            }
            // Fallback: return as-is (uppercase underscore format)
            return strVal.toUpperCase().replace(/[\s-]+/g, "_");
          }
          if (field === "manglik") {
            if (options && Array.isArray(options) && options.length > 0) {
              const normalize = (s: string) => s.toLowerCase().replace(/[_\s]+/g, "");
              const found = options.find((opt) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return normalize(String(optVal)) === normalize(strVal) ||
                       normalize(String(optLabel)) === normalize(strVal);
              });
              if (found) return typeof found === "object" ? found.value : found;
            }
            return strVal;
          }
          if (field === "working_with") {
            if (options && Array.isArray(options) && options.length > 0) {
              const normalize = (s: string) => s.toLowerCase().replace(/[_\s]+/g, "");
              const found = options.find((opt) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;
                return normalize(String(optVal)) === normalize(strVal) ||
                       normalize(String(optLabel)) === normalize(strVal);
              });
              if (found) return typeof found === "object" ? found.value : found;
            }
            return strVal.toUpperCase().replace(/[\s-]+/g, "_");
          }

          // 3. Handle Height/Weight Normalization (Numeric match)
          if (field === "height" || field === "weight") {
            const numVal = Number(strVal.replace(/[^0-9.]/g, ""));
            if (!isNaN(numVal) && numVal > 0) {
              const found = options.find((opt) => {
                const optVal = typeof opt === "object" ? opt.value : opt;
                const optLabel = typeof opt === "object" ? opt.label : opt;

                const optValNum = Number(String(optVal).replace(/[^0-9.]/g, ""));
                // For height label "5' 5\" - 165 cm", we check if it contains the number 165
                return (
                  optValNum === numVal ||
                  String(optLabel).includes(String(numVal))
                );
              });
              if (found) return typeof found === "object" ? found.value : found;
            }
          }

          // 4. Generic Case-Insensitive Match against provided dropdown options
          if (options && Array.isArray(options) && options.length > 0) {
            const found = options.find((opt) => {
              const optVal = typeof opt === "object" ? opt.value : opt;
              return String(optVal).toLowerCase() === strVal.toLowerCase();
            });
            if (found) return typeof found === "object" ? found.value : found;
          }

          return strVal;
        };

        const mappedData = {
          info: {
            name: String(
              getValue("Name") ||
                getValue("name") ||
                getValue("username") ||
                "",
            ),
            email: String(
              getValue("Email") ||
                getValue("email") ||
                getValue("email_address") ||
                "",
            ),
            mobile: String(
              getValue("Mobile") ||
                getValue("mobile") ||
                getValue("Phone") ||
                getValue("phone") ||
                getValue("phone_number") ||
                "",
            ),
            gender: normalizeForDropdown(
              "gender",
              getValue("Gender") || getValue("gender"),
              ["Male", "Female", "Other"],
            ),
            dob: String(getValue("DateOfBirth") || getValue("dob") || ""),
            birth_time: String(
              getValue("BirthTime") || getValue("birth_time") || "",
            ),
            birth_place: String(
              getValue("BirthPlace") || getValue("birth_place") || "",
            ),
          },
          profile: {
            height_cm: normalizeForDropdown(
              "height",
              getValue("HeightCm") ||
                getValue("height_cm") ||
                getValue("Height") ||
                getValue("height"),
              dropdowns?.heightoptions,
            ),
            weight_kg: normalizeForDropdown(
              "weight",
              getValue("WeightKg") ||
                getValue("weight_kg") ||
                getValue("Weight") ||
                getValue("weight"),
              dropdowns?.weightoptions,
            ),
            complexion: normalizeForDropdown(
              "complexion",
              getValue("Complexion") || getValue("complexion"),
              dropdowns?.complexionoptions,
            ),
            marital_status: normalizeForDropdown(
              "marital_status",
              getValue("MaritalStatus") || getValue("marital_status"),
              dropdowns?.maritaloptions,
            ),
            blood_group: normalizeForDropdown(
              "blood_group",
              getValue("BloodGroup") || getValue("blood_group"),
              dropdowns?.bloodgroups,
            ),
            disability_status: normalizeForDropdown(
              "disability",
              getValue("DisabilityStatus") || getValue("disability_status"),
              dropdowns?.disabilities,
            ),
            managed_by: normalizeForDropdown(
              "managed_by",
              getValue("ManagedBy") || getValue("managed_by"),
              dropdowns?.profilemanagedby,
            ),
            severity: String(
              getValue("Severity") || getValue("severity") || "",
            ),
            is_astrology_imp: !!(
              getValue("IsAstrologyImp") || getValue("is_astrology_imp")
            ),
            has_child: !!(getValue("has_child") || getValue("HasChild")),
            intercaste: !!(getValue("Intercaste") || getValue("intercaste")),
            maternal_gotra: String(
              getValue("MaternalGotra") || getValue("maternal_gotra") || "",
            ),
            occupation: normalizeForDropdown(
              "occupation",
              getValue("Occupation") || getValue("occupation"),
              dropdowns?.occupation,
            ),
            qualification: normalizeForDropdown(
              "qualification",
              getValue("Qualification") ||
                getValue("qualification") ||
                getValue("degree"),
              dropdowns?.qualification,
            ),
            working_with: normalizeForDropdown(
              "working_with",
              getValue("WorkingWith") ||
                getValue("working_with") ||
                getValue("working_as"),
              dropdowns?.workingwithoptions,
            ),
            annual_income: normalizeForDropdown(
              "income",
              getValue("AnnualIncome") ||
                getValue("annual_income") ||
                getValue("AnnualIncomeMin") ||
                getValue("annual_income_min") ||
                getValue("income"),
              dropdowns?.incomeoptions,
            ),
            annual_income_min: normalizeForDropdown(
              "income",
              getValue("AnnualIncomeMin") ||
                getValue("annual_income_min") ||
                getValue("MinAnnualIncome") ||
                getValue("min_annual_income"),
              dropdowns?.incomeoptions,
            ),
            annual_income_max: normalizeForDropdown(
              "income",
              getValue("AnnualIncomeMax") ||
                getValue("annual_income_max") ||
                getValue("MaxAnnualIncome") ||
                getValue("max_annual_income"),
              dropdowns?.incomeoptions,
            ),
            family_type: normalizeForDropdown(
              "family_type",
              getValue("FamilyType") || getValue("family_type"),
              dropdowns?.familytypeoptions,
            ),
            family_background: normalizeForDropdown(
              "family_background",
              getValue("FamilyBackground") || getValue("family_background"),
              dropdowns?.familybackgroundoptions,
            ),
            father_name: String(
              getValue("FatherName") || getValue("father_name") || "",
            ),
            mother_name: String(
              getValue("MotherName") || getValue("mother_name") || "",
            ),
            father_occupation: String(
              getValue("FamilyOccupation") ||
                getValue("FatherOccupation") ||
                getValue("father_occupation") ||
                "",
            ),
            mother_occupation: String(
              getValue("MotherOccupation") ||
                getValue("mother_occupation") ||
                "",
            ),
            family_income_min: normalizeForDropdown(
              "income",
              getValue("FamilyIncomeMin") ||
                getValue("family_income_min") ||
                getValue("minFamilyIncome") ||
                getValue("min_family_income"),
              dropdowns?.incomeoptions,
            ),
            family_income_max: normalizeForDropdown(
              "income",
              getValue("FamilyIncomeMax") ||
                getValue("family_income_max") ||
                getValue("maxFamilyIncome") ||
                getValue("max_family_income"),
              dropdowns?.incomeoptions,
            ),
            sibling_count: normalizeForDropdown(
              "sibling",
              getValue("SiblingCount") || getValue("sibling_count"),
              dropdowns?.siblingoptions,
            ),
            sub_caste: String(
              getValue("SubCaste") || getValue("sub_caste") || "",
            ),
            gotra: normalizeForDropdown(
              "gotra",
              getValue("Gotra") || getValue("gotra"),
              dropdowns?.gotra,
            ),
            about_me: String(getValue("AboutMe") || getValue("about_me") || ""),
            about_family: String(
              getValue("AboutFamily") || getValue("about_family") || "",
            ),
            community: normalizeForDropdown(
              "community",
              getValue("Caste") ||
                getValue("community") ||
                getValue("Community"),
              dropdowns?.communities,
            ),
            additional_qualification: normalizeForDropdown(
              "qualification",
              getValue("AdditionalQualification") ||
                getValue("additional_qualification"),
              dropdowns?.qualification,
            ),
            college_name: String(
              getValue("CollegeName") || getValue("college_name") || "",
            ),
            field_of_study: normalizeForDropdown(
              "field_of_study",
              getValue("FieldOfStudy") || getValue("field_of_study"),
              dropdowns?.fieldofstudy,
            ),
            occupation_company: String(
              getValue("OccupationCompany") ||
                getValue("occupation_company") ||
                "",
            ),
            occupation_location: String(
              getValue("OccupationLocation") ||
                getValue("occupation_location") ||
                "",
            ),
            address: String(getValue("Address") || getValue("address") || ""),
            city: String(getValue("City") || getValue("city") || ""),
            state: String(getValue("State") || getValue("state") || ""),
            country: String(getValue("Country") || getValue("country") || ""),
            home_town: String(
              getValue("HomeTown") || getValue("home_town") || "",
            ),
            native_place: String(
              getValue("NativePlace") || getValue("native_place") || "",
            ),
            dietary: normalizeForDropdown(
              "dietary",
              getValue("Dietary") || getValue("dietary"),
              dropdowns?.dietaryoptions,
            ),
            drinking: normalizeForDropdown(
              "drinking",
              getValue("Drinking") || getValue("drinking"),
              dropdowns?.drinkingoptions,
            ),
            smoking: normalizeForDropdown(
              "smoking",
              getValue("Smoking") || getValue("smoking"),
              dropdowns?.smokingoptions,
            ),
            hobbies: Array.isArray(getValue("Hobbies"))
              ? getValue("Hobbies").join(", ")
              : String(getValue("Hobbies") || getValue("hobbies") || ""),
            languages_known: Array.isArray(getValue("LanguagesKnown"))
              ? getValue("LanguagesKnown").join(", ")
              : String(
                  getValue("LanguagesKnown") ||
                    getValue("languages_known") ||
                    "",
                ),
            manglik: normalizeForDropdown(
              "manglik",
              getValue("Manglik") ||
                getValue("manglik") ||
                getValue("IsManglik") ||
                getValue("is_manglik"),
              dropdowns?.manglikoptions,
            ),
            alternate_contact: String(
              getValue("AlternateContact") ||
                getValue("alternate_contact") ||
                "",
            ),
            country_code: String(
              getValue("CountryCode") || getValue("country_code") || "",
            ),
            location_type: String(
              getValue("LocationType") || getValue("location_type") || "",
            ),
            latitude: String(
              getValue("Latitude") || getValue("latitude") || "",
            ),
            longitude: String(
              getValue("Longitude") || getValue("longitude") || "",
            ),
            birth_latitude: String(
              getValue("BirthLatitude") || getValue("birth_latitude") || "",
            ),
            birth_longitude: String(
              getValue("BirthLongitude") || getValue("birth_longitude") || "",
            ),
            is_gunn_matching_important: !!(
              getValue("IsGunnMatchingImportant") ||
              getValue("isGunnMatchingImportant") ||
              getValue("is_gunn_matching_important")
            ),
            is_questionnaire_complete: !!(
              getValue("IsQuestionnaireComplete") ||
              getValue("is_questionnaire_complete")
            ),
            tags_allocated: Array.isArray(getValue("TagsAllocated"))
              ? getValue("TagsAllocated").join(", ")
              : String(
                  getValue("TagsAllocated") || getValue("tags_allocated") || "",
                ),
            children: Array.isArray(getValue("Children"))
              ? getValue("Children")
              : Array.isArray(getValue("children"))
                ? getValue("children")
                : [],
          },
        };

        setFormData(mappedData);
        setInitialFormData(JSON.parse(JSON.stringify(mappedData))); // Store initial data as a deep copy
      } else {
        toast.error("Failed to fetch profile details");
      }
    } catch (error) {
      console.error("Error fetching profile details:", error);
      toast.error("Error fetching profile details");
    } finally {
      setLoading(false);
      LoaderService.hide();
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    const { info } = formData;

    // Mobile validation (Indian 10-digit)
    if (info.mobile && !/^[6-9]\d{9}$/.test(info.mobile)) {
      newErrors.mobile = "Invalid mobile number (10 digits required)";
    }

    // Email validation
    // if (info.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(info.email)) {
    //   newErrors.email = "Invalid email address";
    // }

    // DOB validation (YYYY-MM-DD)
    // if (info.dob && !/^\d{4}-\d{2}-\d{2}$/.test(info.dob)) {
    //   newErrors.dob = "Invalid DOB (YYYY-MM-DD required)";
    // }

    // Time validation (HH:MM or HH:MM:SS)
    // if (info.birth_time && !/^([01]\d|2[0-3]):([0-5]\d)(:([0-5]\d))?$/.test(info.birth_time)) {
    //   newErrors.birth_time = "Invalid time (HH:MM required)";
    // }

    // Place of birth
    // if (!info.birth_place.trim()) {
    //   newErrors.birth_place = "Place of birth is required";
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (
    section: "info" | "profile",
    field: string,
    value: any,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
    // Clear error when user types
    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    try {
      LoaderService.show();

      if (!initialFormData) {
        toast.error("Form data is still loading. Please try again in a moment.");
        LoaderService.hide();
        return;
      }

      // Identify changed fields in info
      const changedInfo: any = {};
      
      // Personal Info Fields - send in profile payload
      if (formData.info.name !== (initialFormData.info?.name ?? "")) changedInfo.name = formData.info.name;
      if (formData.info.gender !== (initialFormData.info?.gender ?? "")) changedInfo.gender = formData.info.gender;
      const normDob = (v: string) => { try { return v ? new Date(v).toISOString().slice(0, 10) : ""; } catch { return v; } };
      if (normDob(formData.info.dob) !== normDob(initialFormData.info?.dob ?? "")) changedInfo.dob = normDob(formData.info.dob) || formData.info.dob;
      if (formData.info.birth_time !== (initialFormData.info?.birth_time ?? "")) changedInfo.birthTime = formData.info.birth_time;
      if (formData.info.birth_place !== (initialFormData.info?.birth_place ?? "")) changedInfo.birthPlace = formData.info.birth_place;
      if (formData.info.email !== (initialFormData.info?.email ?? "")) changedInfo.email_address = formData.info.email;
      if (formData.info.mobile !== (initialFormData.info?.mobile ?? "")) changedInfo.phone_number = Number(formData.info.mobile) || formData.info.mobile;

      // Define mapping function for profile-specific keys
      const getProfilePayload = (profile: typeof formData.profile) => ({
        heightCM: (() => { const m = String(profile.height_cm).match(/(\d+)\s*cm/i); return m ? Number(m[1]) : (Number(profile.height_cm) || profile.height_cm); })(),
        weightKG: (() => { const m = String(profile.weight_kg).match(/(\d+)/); return m ? Number(m[1]) : (Number(profile.weight_kg) || profile.weight_kg); })(),
        maritalStatus: (() => { const v = String(profile.marital_status).toUpperCase().replace(/[\s-]+/g, '_'); return v; })(),
        occupation: profile.occupation,
        city: profile.city,
        state: profile.state,
        nativePlace: profile.native_place,
        hobbies: Array.isArray(profile.hobbies)
          ? profile.hobbies.join(",")
          : profile.hobbies,
        bloodGroup: profile.blood_group,
        managedBy: profile.managed_by,
        disabilityStatus: profile.disability_status,
        complexion: profile.complexion,
        fatherName: profile.father_name,
        motherName: profile.mother_name,
        familyIncomeMin: profile.family_income_min,
        familyIncomeMax: profile.family_income_max,
        familyOccupation: profile.father_occupation,
        motherOccupation: profile.mother_occupation,
        familyType: profile.family_type,
        familyBackground: profile.family_background,
        aboutFamily: profile.about_family,
        siblingCount: profile.sibling_count !== "" ? Number(profile.sibling_count) || profile.sibling_count : "",
        qualification: profile.qualification,
        fieldOfStudy: profile.field_of_study,
        collegeName: profile.college_name,
        additionalQualification: profile.additional_qualification,
        workingWith: (() => { const v = String(profile.working_with).toUpperCase().replace(/[\s-]+/g, '_'); return v; })(),
        occupationCompany: profile.occupation_company,
        occupationLocation: profile.occupation_location,
        annualIncome: profile.annual_income,
        annualIncomeMin: profile.annual_income_min,
        annualIncomeMax: profile.annual_income_max,
        subCaste: profile.sub_caste,
        caste: profile.community,
        gotra: profile.gotra,
        maternalGotra: profile.maternal_gotra,
        intercaste: profile.intercaste,
        languagesKnown: Array.isArray(profile.languages_known)
          ? profile.languages_known.join(",")
          : profile.languages_known,
        alternateContact: profile.alternate_contact,
        aboutMe: profile.about_me,
        drinking: profile.drinking,
        smoking: profile.smoking,
        dietary: profile.dietary,
        countryCode: profile.country_code,
        country: profile.country,
        homeTown: profile.home_town,
        address: profile.address,
        locationType: profile.location_type,
        latitude: Number(profile.latitude) || 0,
        longitude: Number(profile.longitude) || 0,
        birthLatitude: Number(profile.birth_latitude) || 0,
        birthLongitude: Number(profile.birth_longitude) || 0,
        isAstrologyImp: profile.is_astrology_imp,
        isGunnMatchingImportant: profile.is_gunn_matching_important,
        manglik: String(profile.manglik).trim() || "",
      });

      const currentProfilePayload = getProfilePayload(formData.profile);
      const initialProfilePayload = getProfilePayload(initialFormData.profile);

      // Identify changed fields in profile
      const changedProfile: any = {};
      (Object.keys(currentProfilePayload) as Array<keyof typeof currentProfilePayload>).forEach((key) => {
        const currentVal = currentProfilePayload[key];
        const initialVal = initialProfilePayload[key];

        if (JSON.stringify(currentVal) !== JSON.stringify(initialVal)) {
          changedProfile[key] = currentVal;
        }
      });

      // If annualIncome changed but min/max didn't, push it into min/max
      if (changedProfile.annualIncome) {
        if (!changedProfile.annualIncomeMin) changedProfile.annualIncomeMin = changedProfile.annualIncome;
        if (!changedProfile.annualIncomeMax) changedProfile.annualIncomeMax = changedProfile.annualIncome;
        delete changedProfile.annualIncome;
      }

      // Move the personal info fields that belong to the matrimonial profile payload.
      // Phone/email stay in `info` because the backend writes them to the users table.
      if (changedInfo.name) { changedProfile.name = changedInfo.name; delete changedInfo.name; }
      if (changedInfo.gender) { changedProfile.gender = changedInfo.gender; delete changedInfo.gender; }
      if (changedInfo.dob) { changedProfile.dateOfBirth = changedInfo.dob; delete changedInfo.dob; }
      if (changedInfo.birthTime) { changedProfile.birthTime = changedInfo.birthTime; delete changedInfo.birthTime; }
      if (changedInfo.birthPlace) { changedProfile.birthPlace = changedInfo.birthPlace; delete changedInfo.birthPlace; }

      // Convert income range strings to numeric (e.g. "15-20 Lac" -> 1500000)
      const incomeToNumber = (val: string) => {
        if (!val) return val;
        const m = String(val).match(/(\d+)/);
        return m ? Number(m[1]) * 100000 : val;
      };
      if (changedProfile.annualIncomeMin) changedProfile.annualIncomeMin = incomeToNumber(changedProfile.annualIncomeMin);
      if (changedProfile.annualIncomeMax) changedProfile.annualIncomeMax = incomeToNumber(changedProfile.annualIncomeMax);
      if (changedProfile.familyIncomeMin) changedProfile.familyIncomeMin = incomeToNumber(changedProfile.familyIncomeMin);
      if (changedProfile.familyIncomeMax) changedProfile.familyIncomeMax = incomeToNumber(changedProfile.familyIncomeMax);

      const payload = {
        userId: userIdParams,
        info: changedInfo,
        profile: changedProfile,
      };

      // Print payload to console
      console.log("Update Profile Partial Payload:", JSON.stringify(payload, null, 2));

      if (Object.keys(changedProfile).length === 0 && Object.keys(changedInfo).length === 0) {
        toast.error("No changes detected");
        LoaderService.hide();
        return;
      }

      const response = await PostUpdateProfileDetails(payload);
      if (
        response.status === "success" ||
        response.status === true ||
        response.success === true
      ) {
        toast.success("Profile updated successfully");
        navigate(`/users/user/${userIdParams}`);
      } else {
        toast.error(response.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      toast.error("Error updating profile");
    } finally {
      LoaderService.hide();
    }
  };

  if (loading) return null;

  return (
    <div className="min-h-screen bg-[#020617] text-slate-200 p-6 animate-fade-in">
      {/* Header */}
      <div className="max-w-5xl mx-auto flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-slate-800 rounded-full transition-colors text-slate-400 hover:text-white"
          >
            <ChevronLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-white">Edit Profile</h1>
            <p className="text-slate-500 text-sm">
              Update profile information for User ID: {userIdParams}
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg font-bold text-sm transition-all"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold text-sm transition-all flex items-center gap-2 shadow-lg shadow-blue-600/20"
          >
            <Save size={18} /> Update Information
          </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto space-y-6">
        {/* Personal Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("personal")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">👤</span>
              <span className="font-bold text-slate-200">
                Personal Information
              </span>
            </div>
            {expandedSections.personal ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.personal && (
            <div className="p-6 bg-slate-900/20 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Name */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={formData.info.name}
                    onChange={(e) =>
                      handleInputChange("info", "name", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                {/* Gender */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Gender
                  </label>
                  <select
                    value={formData.info.gender}
                    onChange={(e) =>
                      handleInputChange("info", "gender", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                {/* DOB */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Date of Birth (YYYY-MM-DD)
                  </label>
                  <input
                    type="text"
                    placeholder="YYYY-MM-DD"
                    value={formData.info.dob}
                    onChange={(e) =>
                      handleInputChange("info", "dob", e.target.value)
                    }
                    className={`w-full bg-slate-950 border ${errors.dob ? "border-red-500" : "border-slate-700"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all`}
                  />
                  {errors.dob && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {errors.dob}
                    </p>
                  )}
                </div>
                {/* Birth Time */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Birth Time (HH:MM)
                  </label>
                  <input
                    type="text"
                    placeholder="HH:MM"
                    value={formData.info.birth_time}
                    onChange={(e) =>
                      handleInputChange("info", "birth_time", e.target.value)
                    }
                    className={`w-full bg-slate-950 border ${errors.birth_time ? "border-red-500" : "border-slate-700"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all`}
                  />
                  {errors.birth_time && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {errors.birth_time}
                    </p>
                  )}
                </div>
                {/* Birth Place */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Place of Birth
                  </label>
                  <input
                    type="text"
                    value={formData.info.birth_place}
                    onChange={(e) =>
                      handleInputChange("info", "birth_place", e.target.value)
                    }
                    className={`w-full bg-slate-950 border ${errors.birth_place ? "border-red-500" : "border-slate-700"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all`}
                  />
                  {errors.birth_place && (
                    <p className="text-red-500 text-[10px] mt-1">
                      {errors.birth_place}
                    </p>
                  )}
                </div>
                {/* Height */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Height (CM)
                  </label>
                  {dropdownData?.heightoptions?.length > 0 ? (
                    <select
                      value={formData.profile.height_cm}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "height_cm",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Height</option>
                      {renderOptions(dropdownData.heightoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.height_cm}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "height_cm",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Weight */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Weight (KG)
                  </label>
                  <input
                    type="text"
                    placeholder="Weight in KG"
                    value={formData.profile.weight_kg}
                    onChange={(e) =>
                      handleInputChange("profile", "weight_kg", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                {/* Complexion */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Complexion
                  </label>
                  {dropdownData?.complexionoptions?.length > 0 ? (
                    <select
                      value={formData.profile.complexion}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "complexion",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Complexion</option>
                      {renderOptions(dropdownData.complexionoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.complexion}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "complexion",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Marital Status */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Marital Status
                  </label>
                  {dropdownData?.maritaloptions?.length > 0 ? (
                    <select
                      value={formData.profile.marital_status}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "marital_status",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Marital Status</option>
                      {renderOptions(dropdownData.maritaloptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.marital_status}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "marital_status",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Blood Group */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Blood Group
                  </label>
                  {dropdownData?.bloodgroups?.length > 0 ? (
                    <select
                      value={formData.profile.blood_group}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "blood_group",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Blood Group</option>
                      {renderOptions(dropdownData.bloodgroups)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.blood_group}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "blood_group",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Disability */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Disability Status
                  </label>
                  {dropdownData?.disabilities?.length > 0 ? (
                    <select
                      value={formData.profile.disability_status}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "disability_status",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Disability</option>
                      {renderOptions(dropdownData.disabilities)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.disability_status}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "disability_status",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Managed By */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Managed By
                  </label>
                  {dropdownData?.profilemanagedby?.length > 0 ? (
                    <select
                      value={formData.profile.managed_by}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "managed_by",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Managed By</option>
                      {renderOptions(dropdownData.profilemanagedby)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.managed_by}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "managed_by",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                {/* Maternal Gotra */}
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Maternal Gotra
                  </label>
                  <input
                    type="text"
                    value={formData.profile.maternal_gotra}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "maternal_gotra",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                {/* Toggles */}
                <div className="flex items-center gap-6 pt-5">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.profile.is_astrology_imp}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "is_astrology_imp",
                          e.target.checked,
                        )
                      }
                      className="w-4 h-4 rounded border-slate-700 text-blue-600"
                    />
                    <span className="text-xs font-bold text-slate-300">
                      Astrology Imp?
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.profile.has_child}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "has_child",
                          e.target.checked,
                        )
                      }
                      className="w-4 h-4 rounded border-slate-700 text-blue-600"
                    />
                    <span className="text-xs font-bold text-slate-300">
                      Has Child?
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={formData.profile.intercaste}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "intercaste",
                          e.target.checked,
                        )
                      }
                      className="w-4 h-4 rounded border-slate-700 text-blue-600"
                    />
                    <span className="text-xs font-bold text-slate-300">
                      Intercaste OK?
                    </span>
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* About Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("about")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">📝</span>
              <span className="font-bold text-slate-200">About Me</span>
            </div>
            {expandedSections.about ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.about && (
            <div className="p-6 bg-slate-900/20">
              <textarea
                value={formData.profile.about_me}
                onChange={(e) =>
                  handleInputChange("profile", "about_me", e.target.value)
                }
                rows={5}
                className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all resize-none"
                placeholder="Describe the profile..."
              ></textarea>
            </div>
          )}
        </div>

        {/* Professional Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("professional")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">🎓</span>
              <span className="font-bold text-slate-200">
                Professional & Education
              </span>
            </div>
            {expandedSections.professional ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.professional && (
            <div className="p-6 bg-slate-900/20 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Occupation
                </label>
                {dropdownData?.occupation?.length > 0 ? (
                  <select
                    value={formData.profile.occupation}
                    onChange={(e) =>
                      handleInputChange("profile", "occupation", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Occupation</option>
                    {renderOptions(dropdownData.occupation)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.occupation}
                    onChange={(e) =>
                      handleInputChange("profile", "occupation", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Qualification
                </label>
                {dropdownData?.qualification?.length > 0 ? (
                  <select
                    value={formData.profile.qualification}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "qualification",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Qualification</option>
                    {renderOptions(dropdownData.qualification)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.qualification}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "qualification",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Working With
                </label>
                {dropdownData?.workingwithoptions?.length > 0 ? (
                  <select
                    value={formData.profile.working_with}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "working_with",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Working With</option>
                    {renderOptions(dropdownData.workingwithoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.working_with}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "working_with",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Annual Income
                </label>
                {dropdownData?.incomeoptions?.length > 0 ? (
                  <select
                    value={formData.profile.annual_income}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Annual Income</option>
                    {renderOptions(dropdownData.incomeoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.annual_income}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Min Annual Income
                </label>
                {dropdownData?.incomeoptions?.length > 0 ? (
                  <select
                    value={formData.profile.annual_income_min}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income_min",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Min Income</option>
                    {renderOptions(dropdownData.incomeoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.annual_income_min}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income_min",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Max Annual Income
                </label>
                {dropdownData?.incomeoptions?.length > 0 ? (
                  <select
                    value={formData.profile.annual_income_max}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income_max",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Max Income</option>
                    {renderOptions(dropdownData.incomeoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.annual_income_max}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "annual_income_max",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Additional Qualification
                </label>
                {dropdownData?.qualification?.length > 0 ? (
                  <select
                    value={formData.profile.additional_qualification}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "additional_qualification",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Additional Qualification</option>
                    {renderOptions(dropdownData.qualification)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.additional_qualification}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "additional_qualification",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  College Name
                </label>
                <input
                  type="text"
                  value={formData.profile.college_name}
                  onChange={(e) =>
                    handleInputChange("profile", "college_name", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Field of Study
                </label>
                {dropdownData?.fieldofstudy?.length > 0 ? (
                  <select
                    value={formData.profile.field_of_study}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "field_of_study",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Field of Study</option>
                    {renderOptions(dropdownData.fieldofstudy)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.field_of_study}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "field_of_study",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Occupation Company
                </label>
                <input
                  type="text"
                  value={formData.profile.occupation_company}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "occupation_company",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Occupation Location
                </label>
                <input
                  type="text"
                  value={formData.profile.occupation_location}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "occupation_location",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
            </div>
          )}
        </div>

        {/* Family Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("family")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">👨‍👩‍👧‍👦</span>
              <span className="font-bold text-slate-200">
                Family & Background
              </span>
            </div>
            {expandedSections.family ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.family && (
            <div className="p-6 bg-slate-900/20 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Father's Name
                  </label>
                  <input
                    type="text"
                    value={formData.profile.father_name}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "father_name",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Father's Occupation
                  </label>
                  <input
                    type="text"
                    value={formData.profile.father_occupation}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "father_occupation",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Mother's Name
                  </label>
                  <input
                    type="text"
                    value={formData.profile.mother_name}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "mother_name",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Mother's Occupation
                  </label>
                  <input
                    type="text"
                    value={formData.profile.mother_occupation}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "mother_occupation",
                        e.target.value,
                      )
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Family Type
                  </label>
                  {dropdownData?.familytypeoptions?.length > 0 ? (
                    <select
                      value={formData.profile.family_type}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_type",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Family Type</option>
                      {renderOptions(dropdownData.familytypeoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.family_type}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_type",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Family Background
                  </label>
                  {dropdownData?.familybackgroundoptions?.length > 0 ? (
                    <select
                      value={formData.profile.family_background}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_background",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Family Background</option>
                      {renderOptions(dropdownData.familybackgroundoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.family_background}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_background",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Min Family Income
                  </label>
                  {dropdownData?.incomeoptions?.length > 0 ? (
                    <select
                      value={formData.profile.family_income_min}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_income_min",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Min Income</option>
                      {renderOptions(dropdownData.incomeoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.family_income_min}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_income_min",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Max Family Income
                  </label>
                  {dropdownData?.incomeoptions?.length > 0 ? (
                    <select
                      value={formData.profile.family_income_max}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_income_max",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Max Income</option>
                      {renderOptions(dropdownData.incomeoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.family_income_max}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "family_income_max",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Caste / Community
                  </label>
                  {dropdownData?.communities?.length > 0 ? (
                    <select
                      value={formData.profile.community}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "community",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Caste</option>
                      {renderOptions(dropdownData.communities)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.community}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "community",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Gotra
                  </label>
                  {dropdownData?.gotra?.length > 0 ? (
                    <select
                      value={formData.profile.gotra}
                      onChange={(e) =>
                        handleInputChange("profile", "gotra", e.target.value)
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Gotra</option>
                      {renderOptions(dropdownData.gotra)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.gotra}
                      onChange={(e) =>
                        handleInputChange("profile", "gotra", e.target.value)
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Maternal Gotra
                  </label>
                  {dropdownData?.gotra?.length > 0 ? (
                    <select
                      value={formData.profile.maternal_gotra}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "maternal_gotra",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Gotra</option>
                      {renderOptions(dropdownData.gotra)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.maternal_gotra}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "maternal_gotra",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    Sibling Count
                  </label>
                  {dropdownData?.siblingoptions?.length > 0 ? (
                    <select
                      value={formData.profile.sibling_count}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "sibling_count",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    >
                      <option value="">Select Sibling Count</option>
                      {renderOptions(dropdownData.siblingoptions)}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={formData.profile.sibling_count}
                      onChange={(e) =>
                        handleInputChange(
                          "profile",
                          "sibling_count",
                          e.target.value,
                        )
                      }
                      className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                    />
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  About Family
                </label>
                <textarea
                  value={formData.profile.about_family}
                  onChange={(e) =>
                    handleInputChange("profile", "about_family", e.target.value)
                  }
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all resize-none"
                  placeholder="Describe the family..."
                ></textarea>
              </div>
            </div>
          )}
        </div>

        {/* Contact Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("contact")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">📍</span>
              <span className="font-bold text-slate-200">
                Contact & Location
              </span>
            </div>
            {expandedSections.contact ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.contact && (
            <div className="p-6 bg-slate-900/20 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Email Address
                </label>
                <input
                  type="email"
                  value={formData.info.email}
                  onChange={(e) =>
                    handleInputChange("info", "email", e.target.value)
                  }
                  className={`w-full bg-slate-950 border ${errors.email ? "border-red-500" : "border-slate-700"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all`}
                />
                {errors.email && (
                  <p className="text-red-500 text-[10px] mt-1">
                    {errors.email}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Mobile Number
                </label>
                <input
                  type="text"
                  value={formData.info.mobile}
                  onChange={(e) =>
                    handleInputChange("info", "mobile", e.target.value)
                  }
                  className={`w-full bg-slate-950 border ${errors.mobile ? "border-red-500" : "border-slate-700"} rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all`}
                />
                {errors.mobile && (
                  <p className="text-red-500 text-[10px] mt-1">
                    {errors.mobile}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Address
                </label>
                <input
                  type="text"
                  value={formData.profile.address}
                  onChange={(e) =>
                    handleInputChange("profile", "address", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  City
                </label>
                <input
                  type="text"
                  value={formData.profile.city}
                  onChange={(e) =>
                    handleInputChange("profile", "city", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  State
                </label>
                <input
                  type="text"
                  value={formData.profile.state}
                  onChange={(e) =>
                    handleInputChange("profile", "state", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Country
                </label>
                <input
                  type="text"
                  value={formData.profile.country}
                  onChange={(e) =>
                    handleInputChange("profile", "country", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Home Town
                </label>
                <input
                  type="text"
                  value={formData.profile.home_town}
                  onChange={(e) =>
                    handleInputChange("profile", "home_town", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Native Place
                </label>
                <input
                  type="text"
                  value={formData.profile.native_place}
                  onChange={(e) =>
                    handleInputChange("profile", "native_place", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Alternate Contact
                </label>
                <input
                  type="text"
                  value={formData.profile.alternate_contact}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "alternate_contact",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Country Code
                </label>
                <input
                  type="text"
                  value={formData.profile.country_code}
                  onChange={(e) =>
                    handleInputChange("profile", "country_code", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Location Type
                </label>
                <input
                  type="text"
                  value={formData.profile.location_type}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "location_type",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Latitude
                </label>
                <input
                  type="text"
                  value={formData.profile.latitude}
                  onChange={(e) =>
                    handleInputChange("profile", "latitude", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Longitude
                </label>
                <input
                  type="text"
                  value={formData.profile.longitude}
                  onChange={(e) =>
                    handleInputChange("profile", "longitude", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
            </div>
          )}
        </div>

        {/* Lifestyle Section */}
        <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
          <button
            onClick={() => toggleSection("lifestyle")}
            className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="text-xl">🍹</span>
              <span className="font-bold text-slate-200">
                Lifestyle & Extras
              </span>
            </div>
            {expandedSections.lifestyle ? (
              <ChevronUp size={18} />
            ) : (
              <ChevronRight size={18} />
            )}
          </button>
          {expandedSections.lifestyle && (
            <div className="p-6 bg-slate-900/20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Dietary
                </label>
                {dropdownData?.dietaryoptions?.length > 0 ? (
                  <select
                    value={formData.profile.dietary}
                    onChange={(e) =>
                      handleInputChange("profile", "dietary", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Dietary</option>
                    {renderOptions(dropdownData.dietaryoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.dietary}
                    onChange={(e) =>
                      handleInputChange("profile", "dietary", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Drinking
                </label>
                {dropdownData?.drinkingoptions?.length > 0 ? (
                  <select
                    value={formData.profile.drinking}
                    onChange={(e) =>
                      handleInputChange("profile", "drinking", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Drinking</option>
                    {renderOptions(dropdownData.drinkingoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.drinking}
                    onChange={(e) =>
                      handleInputChange("profile", "drinking", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Smoking
                </label>
                {dropdownData?.smokingoptions?.length > 0 ? (
                  <select
                    value={formData.profile.smoking}
                    onChange={(e) =>
                      handleInputChange("profile", "smoking", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Smoking</option>
                    {renderOptions(dropdownData.smokingoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.smoking}
                    onChange={(e) =>
                      handleInputChange("profile", "smoking", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Hobbies
                </label>
                <input
                  type="text"
                  value={formData.profile.hobbies}
                  onChange={(e) =>
                    handleInputChange("profile", "hobbies", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Languages Known
                </label>
                <input
                  type="text"
                  value={formData.profile.languages_known}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "languages_known",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Manglik
                </label>
                {dropdownData?.manglikoptions?.length > 0 ? (
                  <select
                    value={formData.profile.manglik}
                    onChange={(e) =>
                      handleInputChange("profile", "manglik", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  >
                    <option value="">Select Manglik</option>
                    {renderOptions(dropdownData.manglikoptions)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={formData.profile.manglik}
                    onChange={(e) =>
                      handleInputChange("profile", "manglik", e.target.value)
                    }
                    className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                  />
                )}
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Severity
                </label>
                <input
                  type="text"
                  value={formData.profile.severity}
                  onChange={(e) =>
                    handleInputChange("profile", "severity", e.target.value)
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                  Tags Allocated
                </label>
                <input
                  type="text"
                  value={formData.profile.tags_allocated}
                  onChange={(e) =>
                    handleInputChange(
                      "profile",
                      "tags_allocated",
                      e.target.value,
                    )
                  }
                  className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                />
              </div>
              <div className="flex items-center gap-6 pt-5">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.profile.is_gunn_matching_important}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "is_gunn_matching_important",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-700 text-blue-600"
                  />
                  <span className="text-xs font-bold text-slate-300">
                    Gunn Matching Imp?
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.profile.is_questionnaire_complete}
                    onChange={(e) =>
                      handleInputChange(
                        "profile",
                        "is_questionnaire_complete",
                        e.target.checked,
                      )
                    }
                    className="w-4 h-4 rounded border-slate-700 text-blue-600"
                  />
                  <span className="text-xs font-bold text-slate-300">
                    Questionnaire Complete?
                  </span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Children Section */}
        {formData.profile.has_child && (
          <div className="bg-slate-900/30 border border-slate-800/50 rounded-xl overflow-hidden">
            <button
              onClick={() => toggleSection("children" as any)}
              className="w-full flex items-center justify-between p-4 bg-slate-800/30 hover:bg-slate-800/50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="text-xl">👶</span>
                <span className="font-bold text-slate-200">
                  Children Details
                </span>
              </div>
              {expandedSections["children" as keyof typeof expandedSections] ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronRight size={18} />
              )}
            </button>
            {expandedSections["children" as keyof typeof expandedSections] && (
              <div className="p-6 bg-slate-900/20 space-y-6">
                {(formData.profile.children || []).map(
                  (child: any, index: number) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 md:grid-cols-2 gap-6 p-4 border border-slate-800 rounded-xl bg-slate-950/30"
                    >
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Child {index + 1} Name
                        </label>
                        <input
                          type="text"
                          value={child.name || child.Name || ""}
                          onChange={(e) => {
                            const newChildren = [...formData.profile.children];
                            newChildren[index] = {
                              ...child,
                              name: e.target.value,
                            };
                            handleInputChange(
                              "profile",
                              "children",
                              newChildren,
                            );
                          }}
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                        />
                      </div>
                      <div className="space-y-2">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                          Child {index + 1} Age
                        </label>
                        <input
                          type="text"
                          value={child.age || child.Age || ""}
                          onChange={(e) => {
                            const newChildren = [...formData.profile.children];
                            newChildren[index] = {
                              ...child,
                              age: e.target.value,
                            };
                            handleInputChange(
                              "profile",
                              "children",
                              newChildren,
                            );
                          }}
                          className="w-full bg-slate-950 border border-slate-700 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 transition-all"
                        />
                      </div>
                    </div>
                  ),
                )}
                <button
                  onClick={() => {
                    const newChildren = [
                      ...(formData.profile.children || []),
                      { name: "", age: "" },
                    ];
                    handleInputChange("profile", "children", newChildren);
                  }}
                  className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-bold transition-all"
                >
                  + Add Child
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Floating Save Button for Mobile */}
      <div className="lg:hidden fixed bottom-6 right-6">
        <button
          onClick={handleSubmit}
          className="w-14 h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-full flex items-center justify-center shadow-2xl shadow-blue-600/40 transition-all active:scale-95"
        >
          <Save size={24} />
        </button>
      </div>
    </div>
  );
};

export default EditProfileScreen;


